import type { AssigneesData, ReleaseDocument } from "@packages/commons";
import type { AuthUser } from "@/app/providers/services";
import { getCurrentUser } from "@/global";

/**
 * Anything that looks like a user — covers both the API-layer `AuthUser`
 * (snake_case) and the task/assignee `AssigneesData` (camelCase), as well as
 * a bare string identity/email that is sometimes used when full user data
 * hasn't been resolved yet.
 */
export type UserLike = AssigneesData | AuthUser;

function isAuthUser(u: UserLike): u is AuthUser {
  return "first_name" in u;
}

/**
 * Returns a human-readable display name for a user.
 *
 * Resolution order:
 * - `AuthUser`: `first_name + last_name` → `email`
 * - `AssigneesData`: `name` → `full_name` → `firstName + lastName` → `email`
 */
export function getUserDisplayName(user: UserLike): string {
  if (isAuthUser(user)) {
    const full = [user.first_name ?? "", user.last_name ?? ""]
      .map((s) => s.trim())
      .filter(Boolean)
      .join(" ");
    return full || user.email || "";
  }

  if (user.name) return user.name;
  if (user.full_name) return user.full_name;

  const full = [user.firstName ?? "", user.lastName ?? ""]
    .map((s) => s.trim())
    .filter(Boolean)
    .join(" ");
  return full || user.email || "";
}

/**
 * Derives 1–2 character initials for an avatar from a user or a bare string.
 *
 * Resolution order:
 * - Full name → first char of first word + first char of last word
 * - Single-word name → first 2 chars
 * - Email → first 2 chars of local part
 * - Bare string → first 2 chars of local part (if contains `@`) or first 2 chars
 */
export function getUserInitials(user: UserLike | string): string {
  if (typeof user === "string") {
    return user.includes("@")
      ? user.split("@")[0].substring(0, 2).toUpperCase()
      : user.substring(0, 2).toUpperCase();
  }

  const name = getUserDisplayName(user);
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  }

  const email = user.email;
  if (email) return email.split("@")[0].substring(0, 2).toUpperCase();

  const id = (user as any).id ?? "";
  return id.substring(0, 2).toUpperCase();
}

/**
 * Returns the avatar URL for a user, or `undefined` if none is set.
 *
 * Filters out the common placeholder value `"/"` used in some legacy code.
 */
export function getUserAvatar(user: UserLike): string | undefined {
  if (isAuthUser(user)) {
    const url = (user as any).avatar_url;
    return url && url !== "/" ? url : undefined;
  }
  const av = user.avatar;
  return av && av !== "/" ? av : undefined;
}

/**
 * Returns the current user's identity ID as the backend stores it.
 *
 * The backend sets `identity_id = token.oid || token.sub` when validating the
 * bearer token (see azureAuth.js BearerStrategy callback). We mirror that
 * fallback here: `oid` is the stable Azure AD Object ID; `sub` is the
 * pairwise subject ID used when `oid` is not present in the token (e.g. after
 * optional-claims configuration changes in the app registration).
 */
export function getCurrentIdentityId(): string | undefined {
  const profile = getCurrentUser()?.profile;
  return (profile?.oid ?? profile?.sub) as string | undefined;
}

/**
 * Returns true when the given file lock belongs to the currently authenticated
 * user. Pass `document.locked` directly — returns false for unlocked files.
 */
export function isLockedByMe(lock: ReleaseDocument["locked"]): boolean {
  if (!lock) return false;
  return lock.identity_id === getCurrentIdentityId();
}

/**
 * Returns true when a file is locked by someone *other* than the current user,
 * i.e. the lock should block editing. Returns false for both unlocked files and
 * files locked by the current user.
 */
export function isLockedByOther(lock: ReleaseDocument["locked"]): boolean {
  if (!lock) return false;
  return lock.identity_id !== getCurrentIdentityId();
}
